import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Receipt, Search, Eye, CheckCircle2, Plus, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "sonner";
import { breakdownInclusive, EGYPT_VAT_RATE, formatCurrency } from "@/lib/currency";
import SubscriptionInvoiceDialog, { SubscriptionInvoiceData } from "@/components/accountant/SubscriptionInvoiceDialog";

const PLAN_LABELS: Record<string, string> = {
  basic: "Starter — النظام الإداري",
  standard: "Growth — إداري + تعليمي",
  premium: "Pro — النظام الكامل",
  free: "مجاني",
  enterprise: "مؤسسي",
};

const MONTHS_AR = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

const AccountantSubscriptionInvoices = () => {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<SubscriptionInvoiceData | null>(null);

  const { data: rows, isLoading } = useQuery({
    queryKey: ["accountant-sub-invoices"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nursery_subscription_invoices" as any)
        .select(`
          id, nursery_id, subscription_id, month, year, pricing_model,
          children_count, price_per_child, amount, status, due_date,
          paid_at, created_at, vat_rate, currency,
          nurseries:nursery_id(name, address, tax_number),
          subscription:nursery_subscriptions!subscription_id(plan, billing_cycle)
        `)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as any[];
    },
  });

  const markPaid = useMutation({
    mutationFn: async (id: string) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("nursery_subscription_invoices" as any)
        .update({
          status: "paid",
          paid_at: new Date().toISOString(),
          confirmed_by: u?.user?.id ?? null,
          confirmed_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("تم تأكيد التحصيل ✓");
      qc.invalidateQueries({ queryKey: ["accountant-sub-invoices"] });
    },
    onError: (e: any) => toast.error(e?.message || "فشل تأكيد التحصيل"),
  });

  const filtered = (rows || []).filter((r) => {
    if (filterStatus !== "all" && r.status !== filterStatus) return false;
    if (search && !r.nurseries?.name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalGross = filtered.reduce((s, r) => s + Number(r.amount || 0), 0);
  const totalVat = filtered.reduce(
    (s, r) => s + breakdownInclusive(Number(r.amount || 0), Number(r.vat_rate || EGYPT_VAT_RATE)).vatAmount,
    0
  );
  const totalNet = totalGross - totalVat;
  const totalPaid = filtered
    .filter((r) => r.status === "paid")
    .reduce((s, r) => s + Number(r.amount || 0), 0);

  const openDetails = (r: any) => {
    setSelected({
      id: r.id,
      nursery_name: r.nurseries?.name || "—",
      nursery_address: r.nurseries?.address,
      nursery_tax_number: r.nurseries?.tax_number,
      plan_label: PLAN_LABELS[r.subscription?.plan] || r.subscription?.plan || "اشتراك",
      billing_cycle: r.subscription?.billing_cycle,
      pricing_model: r.pricing_model,
      children_count: r.children_count,
      price_per_child: r.price_per_child,
      month: r.month,
      year: r.year,
      amount: Number(r.amount || 0),
      vat_rate: Number(r.vat_rate ?? EGYPT_VAT_RATE),
      status: r.status,
      due_date: r.due_date,
      paid_at: r.paid_at,
      created_at: r.created_at,
    });
    setOpen(true);
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Receipt className="w-6 h-6 text-primary" />
          فواتير اشتراكات الحضانات
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          الفواتير المُصدَرة على الحضانات مقابل اشتراكها في المنصة — بالجنيه المصري وضريبة القيمة المضافة {EGYPT_VAT_RATE}%.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">إجمالي الفوترة</p>
            <p className="text-xl font-bold">{formatCurrency(totalGross)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">صافي قبل الضريبة</p>
            <p className="text-xl font-bold text-blue-600">{formatCurrency(totalNet)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">ضريبة القيمة المضافة</p>
            <p className="text-xl font-bold text-amber-600">{formatCurrency(totalVat)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">المحصّل</p>
            <p className="text-xl font-bold text-green-600">{formatCurrency(totalPaid)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="بحث باسم الحضانة..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-9" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الحالات</SelectItem>
                <SelectItem value="unpaid">غير مدفوعة</SelectItem>
                <SelectItem value="paid">مدفوعة</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">الفواتير ({filtered.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center py-8 text-muted-foreground">جاري التحميل...</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>الحضانة</TableHead>
                    <TableHead>الباقة</TableHead>
                    <TableHead>الشهر</TableHead>
                    <TableHead>صافي</TableHead>
                    <TableHead>ض.ق.م</TableHead>
                    <TableHead>الإجمالي</TableHead>
                    <TableHead>الاستحقاق</TableHead>
                    <TableHead>الحالة</TableHead>
                    <TableHead>إجراء</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r: any) => {
                    const v = breakdownInclusive(Number(r.amount || 0), Number(r.vat_rate || EGYPT_VAT_RATE));
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium">{r.nurseries?.name || "—"}</TableCell>
                        <TableCell className="text-xs">
                          <Badge variant="outline">{PLAN_LABELS[r.subscription?.plan] || r.subscription?.plan || "—"}</Badge>
                        </TableCell>
                        <TableCell className="text-xs">{MONTHS_AR[r.month - 1]} {r.year}</TableCell>
                        <TableCell className="text-xs">{formatCurrency(v.subtotal)}</TableCell>
                        <TableCell className="text-xs text-amber-600">{formatCurrency(v.vatAmount)}</TableCell>
                        <TableCell className="font-bold">{formatCurrency(v.total)}</TableCell>
                        <TableCell className="text-xs">{format(new Date(r.due_date), "dd MMM yyyy", { locale: ar })}</TableCell>
                        <TableCell>
                          <Badge className={r.status === "paid"
                            ? "bg-green-100 text-green-800"
                            : "bg-amber-100 text-amber-800"}>
                            {r.status === "paid" ? "مدفوعة" : "غير مدفوعة"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => openDetails(r)}>
                              <Eye className="w-3 h-3" /> عرض
                            </Button>
                            {r.status !== "paid" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 gap-1 text-xs text-green-700 border-green-300 hover:bg-green-50"
                                onClick={() => markPaid.mutate(r.id)}
                                disabled={markPaid.isPending}
                              >
                                <CheckCircle2 className="w-3 h-3" /> دفع
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!filtered.length && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        لا توجد فواتير اشتراكات
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <SubscriptionInvoiceDialog
        open={open}
        onOpenChange={setOpen}
        invoice={selected}
      />
    </div>
  );
};

export default AccountantSubscriptionInvoices;
